// @ts-check
import _ from 'lodash'

/**
 * @type {Record<string, Record<string, any>>}
 */
export const allQueries = {}

/**
 * @type {Record<string, Record<string, any>>}
 */
export const allMutations = {}

/**
 * @typedef {import('src/models/model/model').default} Model
 */

/**
 * A map of model classes keyed by their class name.
 * @type {Record<string, Model>}
 */
export const TYPE_NAME_TO_MODEL_MAP = {}

export const CONSTRUCTOR_OPTION_KEYS = Object.freeze([
  'parent', 'key', 'fields', 'queryName', 'resultsName', 'variables', 'isCollection',
  'missingDataPolicy', 'extraDataPolicy', 'sortOrder', 'editing', 'nonReactive', 'autoSave',
  'watchQuery', 'forceLanguage'
])

/**
 * The important concept here for primitive types is that they are immutable.
 * We made that assumption and don't copy them when cloning and saving an
 * original copy for checking dirty.
 */
export const PRIMITIVE_TYPES = Object.freeze(['ID', 'String', 'Number', 'Integer', 'Int', 'Boolean', 'IsoDate', 'IsoDateTime'])

export const SIMPLE_TYPES = Object.freeze(['Object', 'Array', 'JsonObject', 'JsonArray', 'LocalizedJsonObject'])

export const TRANSLATABLE_SUB_KEY_TYPES = Object.freeze(['JsonObject', 'JsonArray', 'LocalizedJsonObject'])

export const MODEL_LEVEL = Object.freeze({
  SITE: 'site',
  CIRCUIT: 'circuit'
})

/**
 * The different modes for displaying time.
 */
export const TIMEZONE_MODES = Object.freeze({
  SITE: 'site',
  LOCAL: 'local',
  RELATIVE: 'relative'
})

export const msPerSecond = 1000
export const msPerMinute = 60 * msPerSecond
export const msPerHour = msPerMinute * 60
export const msPerDay = msPerHour * 24
export const msPerMonth = msPerDay * 30
export const msPerYear = msPerDay * 365

/**
 * Wait 6 seconds because the reconnect message is shown for 5 seconds.
 */
export const RETRY_DELAY_MS = 6000

export const MAX_RETRY_COUNT = 50

export const encoders = Object.freeze({
  default (data, name, value) {
    data[name] = value
  },
  hasOne (data, name) {
    if (this[name] && this[name].id > 0) {
      data[`${name}Id`] = this[name].id
    } else {
      data[`${name}Id`] = null
    }
  },
  cents (data, name) {
    const value = this[name]
    data[`${name}Cents`] = (value === null || value === undefined) ? value : Math.round(value * 100)
  },
  centsHundredths (data, name) {
    const value = this[name]
    data[`${name}CentsHundredths`] = (value === null || value === undefined) ? value : Math.round(value * 10000)
  },
  if (condition) {
    return function (data, name, value) {
      if (condition.call(this)) {
        encoders.default(data, name, value)
      }
    }
  }
})

export const decoders = Object.freeze({
  default (data, name, value) {
    data[name] = value
  }
})

export const rules = Object.freeze({
  required: val => val !== null || 'Field cannot be blank',
  positive: val => val === null || val > 0 || 'Must be positive value',
  number: val => val === null || _.isNumber(val) || 'Must be a number',
  integer: val => val === null || _.isInteger(val) || 'Must be an integer'
})
